import React, { useMemo } from 'react';
import {
  Box, Tab, Tabs, Typography,
} from '@mui/material';

import { MonitorOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import withProfile from '../withProfile';
import SmallScreenPlaceholder from '../SmallScreenPlaceholder';
import ExperienceGraphsTab from './ExperienceGraphsTab';
import BehaviorConcernsGraphsTab from './BehaviorConcernsGraphsTab';
import DayTenTab from './DayTenTab';
import { useAwoContext } from '../AwoContextProvider';
import { getDayAdopter10CheckinIds, getDayFoster10CheckinIds, getExperienceTypeformIds } from '../../utilities';

enum FeedbackTabs {
  DAY10_ADOPTER = 'day-10-adopter-surveys',
  DAY10_FOSTER = 'day-10-foster-surveys',
  EXPERIENCE_SURVEYS = 'experience-surveys',
  BEHAVIOR_CONCERNS = 'behavior-concerns',
}

function FeedbackContainer(
  { setDrawerOpen, setUserId }:
    { setDrawerOpen: (value: boolean) => void; setUserId: (id: number) => void },
) {
  const { awo } = useAwoContext();
  const { tab } = useParams();
  const navigate = useNavigate();
  const isProduction = window.location.hostname.includes('app.petcademy');
  const adopterTypeformIds = getDayAdopter10CheckinIds(isProduction);
  const fosterTypeformIds = getDayFoster10CheckinIds(isProduction);
  const experienceTypeformIds = getExperienceTypeformIds(isProduction);

  const [selectedTabValue] = React.useState<string>(tab || FeedbackTabs.BEHAVIOR_CONCERNS);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    navigate(`/feedback/${newValue}`);
  };

  const handleCardClick = (petParentId: number) => {
    setUserId(petParentId);
    setDrawerOpen(true);
  };

  const tabsOptions = [
    {
      label: 'Behavior Concerns',
      value: FeedbackTabs.BEHAVIOR_CONCERNS,
      showTab: true,
    },
    {
      label: 'Day 10 Adopter Surveys',
      value: FeedbackTabs.DAY10_ADOPTER,
      showTab: awo.dogAdopt || awo.catAdopt,
    },
    {
      label: 'Day 10 Foster Surveys',
      value: FeedbackTabs.DAY10_FOSTER,
      showTab: awo.dogFoster || awo.catFoster,
    },
    {
      label: 'Experience Surveys',
      value: FeedbackTabs.EXPERIENCE_SURVEYS,
      showTab: true,
    },
  ];

  return (
    <>
      <Box
        sx={{
          padding: 3,
          minHeight: '100vh',
          display: {
            xs: 'block',
            md: 'none',
          },
        }}
      >
        <Typography variant="overline">Feedback</Typography>
        <Box sx={{ pt: 3 }}>
          <SmallScreenPlaceholder icon={<MonitorOutlined />} text="To view the charts, access Petcademy on a larger screen" />
        </Box>
      </Box>
      <Box>
        <Box sx={{
          display: {
            xs: 'none',
            md: 'flex',
          },
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
        }}
        >
          <Typography variant="overline">Feedback</Typography>
          <Box sx={{
            pt: 1.5,
          }}
          >
            <Tabs value={selectedTabValue} onChange={handleChange} aria-label="shelter settings">
              {
                tabsOptions
                  .filter((tabOption) => tabOption.showTab)
                  .map((tabOption) => (
                    <Tab label={tabOption.label} key={tabOption.label} value={tabOption.value} />
                  ))
              }
            </Tabs>
          </Box>
        </Box>
        {
          selectedTabValue === FeedbackTabs.BEHAVIOR_CONCERNS && (
            <BehaviorConcernsGraphsTab
              awoName={awo.name}
              dogAdopterTypeformId={parseInt(adopterTypeformIds[1], 10)}
              catAdopterTypeformId={parseInt(adopterTypeformIds[0], 10)}
              dogFosterTypeformId={parseInt(fosterTypeformIds[1], 10)}
              catFosterTypeformId={parseInt(fosterTypeformIds[0], 10)}
              showDogs={awo.dogAdopt || awo.dogFoster}
              showCats={awo.catAdopt || awo.catFoster}
            />
          )
        }
        {
          selectedTabValue === FeedbackTabs.DAY10_ADOPTER && (
            <DayTenTab
              awoId={awo.id}
              typeformIds={adopterTypeformIds}
              careType="Adopted"
              handleCardClick={handleCardClick}
            />
          )
        }
        {
          selectedTabValue === FeedbackTabs.DAY10_FOSTER && (
            <DayTenTab
              awoId={awo.id}
              typeformIds={fosterTypeformIds}
              careType="Foster"
              handleCardClick={handleCardClick}
            />
          )
        }
        {
          selectedTabValue === FeedbackTabs.EXPERIENCE_SURVEYS && (
            <ExperienceGraphsTab
              awoId={awo.id}
              awoName={awo.name}
              adopterTypeformId={experienceTypeformIds[0]}
              fosterTypeformId={experienceTypeformIds[1]}
              handleCardClick={handleCardClick}
            />
          )
        }
      </Box>
    </>
  );
}

export default withProfile(FeedbackContainer);
